import React from "react";
import {Box, Typography, List, ListItem, Grid, Paper} from "@mui/material";
import {ServiceDetails} from "shared-lib";
import {AttachmentGallery} from "shared-lib";
import {
  LocationOn as LocationOnIcon,
  Article as ArticleIcon,
  Build as BuildIcon,
  Image as ImageIcon,
} from "@mui/icons-material";

interface ServiceDetailsRendererProps {
  serviceDetails: ServiceDetails;
}

// Format field name to be more readable
const formatFieldName = (fieldName: string): string => {
  // Handle camelCase - insert space before capital letters
  let formatted = fieldName.replace(/([A-Z])/g, ' $1');
  
  // Handle snake_case - replace underscores with spaces
  formatted = formatted.replace(/_/g, ' ');
  
  // Trim and capitalize each word
  return formatted
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const ServiceDetailsRenderer: React.FC<ServiceDetailsRendererProps> = ({serviceDetails}) => {
  if (!serviceDetails) {
    return null;
  }

  // Handle types properly for backward compatibility
  const detailsAny = serviceDetails as any;
  const hasAttachments =
    detailsAny.attachments && Array.isArray(detailsAny.attachments) && detailsAny.attachments.length > 0;
  const hasPhotoUrls = detailsAny.photoUrls && Array.isArray(detailsAny.photoUrls) && detailsAny.photoUrls.length > 0;

  // Try to parse JSON description for new model
  let parsedDescription = null;
  try {
    if (serviceDetails.description) {
      parsedDescription = JSON.parse(serviceDetails.description);
    }
  } catch (error) {
    console.error("Error parsing service details JSON:", error);
    // Will fall back to displaying raw description
  }

  return (
    <Box sx={{width: "100%"}}>
      {/* If we have parsed JSON description, display structured data */}
      {parsedDescription ? (
        <>
          {/* Water Location */}
          {parsedDescription.waterLocation && (
            <Box sx={{mb: 3}}>
              <Typography 
                variant="subtitle2" 
                sx={{
                  fontWeight: 600, 
                  mb: 1,
                  display: "flex",
                  alignItems: "center"
                }}>
                <LocationOnIcon color="primary" sx={{mr: 1, fontSize: "1.1rem"}} />
                Water Location
              </Typography>
              <Paper 
                elevation={0} 
                sx={{
                  p: 2, 
                  bgcolor: "rgba(16, 29, 59, 0.03)", 
                  borderRadius: "8px", 
                  border: "1px solid rgba(16, 29, 59, 0.06)",
                  '&:hover': {
                    bgcolor: "rgba(16, 29, 59, 0.04)",
                  }
                }}>
                <Typography variant="body2" sx={{color: "rgba(16, 29, 59, 0.7)"}}>
                  {parsedDescription.waterLocation}
                </Typography>
              </Paper>
            </Box>
          )}
          
          {/* Service Description */}
          {parsedDescription.serviceDescription && (
            <Box sx={{mb: 3}}>
              <Typography 
                variant="subtitle2" 
                sx={{
                  fontWeight: 600, 
                  mb: 1,
                  display: "flex",
                  alignItems: "center"
                }}>
                <ArticleIcon color="primary" sx={{mr: 1, fontSize: "1.1rem"}} />
                Additional information
              </Typography>
              <Paper 
                elevation={0} 
                sx={{
                  p: 2, 
                  bgcolor: "rgba(16, 29, 59, 0.03)", 
                  borderRadius: "8px", 
                  border: "1px solid rgba(16, 29, 59, 0.06)",
                  '&:hover': {
                    bgcolor: "rgba(16, 29, 59, 0.04)",
                  }
                }}>
                <Typography variant="body2" sx={{color: "rgba(16, 29, 59, 0.7)", whiteSpace: "pre-wrap"}}>
                  {parsedDescription.serviceDescription}
                </Typography>
              </Paper>
            </Box>
          )}
          
          {/* Services Details */}
          {parsedDescription.services && Object.keys(parsedDescription.services).length > 0 && (
            <Box sx={{mb: 3}}>
              <Typography 
                variant="subtitle2" 
                sx={{
                  fontWeight: 600, 
                  mb: 1,
                  display: "flex",
                  alignItems: "center"
                }}>
                <BuildIcon color="primary" sx={{mr: 1, fontSize: "1.1rem"}} />
                Service Specifications
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(parsedDescription.services).map(([serviceName, serviceData]: [string, any]) => (
                  <Grid item xs={12} key={serviceName}>
                    <Paper 
                      elevation={0} 
                      sx={{
                        p: 2, 
                        mb: 1,
                        bgcolor: "rgba(16, 29, 59, 0.03)", 
                        borderRadius: "8px", 
                        border: "1px solid rgba(16, 29, 59, 0.06)",
                        '&:hover': {
                          bgcolor: "rgba(16, 29, 59, 0.04)",
                        }
                      }}
                    >
                      <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1, color: "primary.main"}}>
                        {formatFieldName(serviceName)}
                      </Typography>
                      <Grid container spacing={2}>
                        {Object.entries(serviceData).map(([fieldName, fieldValue]: [string, any]) => (
                          <Grid item xs={12} sm={6} md={6} key={fieldName}>
                            <Box 
                              sx={{
                                p: 1.5, 
                                borderRadius: "6px", 
                                bgcolor: "rgba(255,255,255,0.6)",
                                border: "1px solid rgba(16, 29, 59, 0.04)",
                              }}
                            >
                              <Typography 
                                variant="caption" 
                                sx={{
                                  display: "block", 
                                  color: "text.secondary", 
                                  mb: 0.5,
                                  fontSize: "0.7rem",
                                  fontWeight: 500
                                }}
                              >
                                {formatFieldName(fieldName)}
                              </Typography>
                              <Typography variant="body2" sx={{fontWeight: 500, color: "rgba(16, 29, 59, 0.8)"}}>
                                {fieldValue}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </>
      ) : (
        // Fallback to display raw description as before
      <List dense disablePadding>
        {/* Show service description if available */}
        {serviceDetails.description && (
          <ListItem disablePadding sx={{mb: 1}}>
            <Box sx={{width: "100%"}}>
              <Typography
                variant="body2"
                component="span"
                sx={{fontWeight: 600, color: "rgba(16, 29, 59, 0.6)", mr: 1, display: "block"}}>
                Description:
              </Typography>
              <Typography variant="body2" sx={{mt: 1}}>
                {serviceDetails.description}
              </Typography>
            </Box>
          </ListItem>
          )}
        </List>
        )}

        {/* Show attachments if available */}
        {(hasAttachments || hasPhotoUrls) && (
        <Box sx={{mt: 2}}>
              <Typography
            variant="subtitle2" 
            sx={{
              fontWeight: 600, 
              mb: 1,
              display: "flex",
              alignItems: "center"
            }}>
            <ImageIcon color="primary" sx={{mr: 1, fontSize: "1.1rem"}} />
            Attachments
              </Typography>
              {hasAttachments ? <AttachmentGallery attachments={detailsAny.attachments} /> : <></>}
            </Box>
        )}
    </Box>
  );
};

export default ServiceDetailsRenderer;
